import { UnregesterdBeaconListResponse, UnregisteredBeacon } from "types/beacon";
import { RootStore } from "./RootStore";
import { action, computed, makeObservable, observable } from "mobx";
import { createBeacon, getUnregisteredBeaconList } from "api/beacons.api";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { min } from "lodash";
import { FieldState, FormState } from "formstate";
import { MinimumLengthValidation, required } from "utils/formstate";
import { AssetDto } from "types/asset";
import { getUUID } from "utils/utils";
import { CreateAsset, isAssetIdExists, isAssetNameExists } from "api/assets.api";
import { NUMBER_OF_ROWS_PER_PAGE } from "config/constants";

export class UnregisteredBeaconStore {

  root: RootStore;
  loading = true;
  error: ErrorResponseDto | null = null
  beaconList: UnregisteredBeacon[] = [];
  beaconForm = this.createForm();
  selectedBeaconToCreateAsset: UnregisteredBeacon | null = null

  totalCount = 0
  totalUnfilterCount = 0
  nextToken: string = ""
  searchTerm: string = ""

  createForm() {
    return new FormState({
      asset_name: new FieldState('').validators(required, MinimumLengthValidation),
      asset_type: new FieldState('').validators(required),
      owner: new FieldState('').validators(required),
      description: new FieldState(''),
    });
  }

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      loading: observable,
      error: observable,
      beaconList: observable,
      totalCount: observable,
      totalUnfilterCount: observable,
      nextToken: observable,
      searchTerm: observable,
      beaconForm: observable,
      selectedBeaconToCreateAsset: observable,

      reset: action.bound,
      loadUnregisteredBeacons: action.bound,
      setSearchTerm: action.bound,
      setSelectedBeaconToCreateAsset: action.bound,
      createBeacon: action.bound,
      importAndCreate: action.bound,

      nextCount: computed,
      getEmptyListMsg: computed,
    });
  }

  reset() {
    this.loading = true;
    this.beaconList = [];
    this.error = null
    this.totalCount = 0
    this.totalUnfilterCount = 0
    this.nextToken = ""
    this.searchTerm = ""
    this.selectedBeaconToCreateAsset = null
  }

  setSearchTerm(value: string) {
    this.searchTerm = value
  }

  setSelectedBeaconToCreateAsset(beacon: UnregisteredBeacon | null) {
    this.selectedBeaconToCreateAsset = beacon
    this.beaconForm = this.createForm();
    this.beaconForm.validate()
  }

  getLoadParams(firstPage: boolean) {
    const params: Record<string, any> = {
      limit: NUMBER_OF_ROWS_PER_PAGE,
    }
    if (!firstPage && this.nextToken !== "")
      params.next = this.nextToken

    if (this.searchTerm !== "") {
      params["beacon_id_filter"] = this.searchTerm
    }

    return params
  }

  loadUnregisteredBeacons(firstPage = false) {
    if (!this.root.hasBeaconIntegration) {
      this.loading = false
      return Promise.resolve()
    }

    this.loading = true
    this.error = null
    const params = this.getLoadParams(firstPage)
    return getUnregisteredBeaconList(this.root.beaconIntegration, params)
      .then((res: UnregesterdBeaconListResponse) => {
        if (firstPage)
          this.beaconList = [...res.items]
        else
          this.beaconList = [...this.beaconList, ...res.items]
        this.totalCount = res.total
        this.nextToken = res.next
        this.error = null
        if (!("beacon_id_filter" in params)) {
          this.totalUnfilterCount = res.total
        }
      })
      .catch((err: ErrorResponseDto) => {
        if (err.statusCode === 503) {
          this.error = new ErrorResponseDto(
            "Cannot retrieve new beacons currently. The third party system may be unavailable, please try again later.")
        }
        else
          this.error = err
      })
      .finally(() => this.loading = false);
  }

  BuildAssetDto() {
    const obj = new AssetDto();
    obj.data.asset_id = getUUID()
    obj.data.asset_name = this.beaconForm.$.asset_name.value
    obj.data.asset_type_id = this.beaconForm.$.asset_type.value
    obj.data.owner_id = this.beaconForm.$.owner.value
    obj.data.description = this.beaconForm.$.description.value
    return obj
  }

  createBeacon(beacon: UnregisteredBeacon) {
    const date = new Date();
    return createBeacon({
      ...beacon,
      import_info: {
        import_timestamp: date.toISOString(),
        source: beacon.source,
        order_id: beacon.order_id,
      }
    })
  }

  async importAndCreate() {
    if (!this.selectedBeaconToCreateAsset)
      return

    const obj = this.BuildAssetDto()
    const duplicateName = await isAssetNameExists("", obj.data.asset_name)
    if (duplicateName) {
      this.beaconForm.$.asset_name.setError("Asset name specified already exists.")
      return Promise.reject('validationError')
    }
    while (true) {
      const duplicateId = await isAssetIdExists(obj.data.asset_id)
      if (duplicateId) {
        obj.data.asset_id = getUUID()
      }
      else
        break
    }

    return this.createBeacon(this.selectedBeaconToCreateAsset)
      .then(() => {
        obj.data.assigned_beacon_id = this.selectedBeaconToCreateAsset?.beacon_id || ""
        return CreateAsset(obj.data)
      })
  }

  get getEmptyListMsg() {
    if (this.searchTerm === "")
      return "No Beacons to display"
    else
      return `No beacons found. Please check you have correctly entered the full beacon ID.`
  }

  get nextCount() {
    return min([this.totalCount - this.beaconList.length, NUMBER_OF_ROWS_PER_PAGE]) || 0
  }

}